import { useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { StageColorField } from './StageColorField.jsx';
import { boardColumns, nextStageColor, stageColor } from '../utils/taskProgress.js';

function draftFromColumns(columns) {
  return boardColumns(columns).map((column) => ({
    id: column.columnId,
    name: column.name,
    color: stageColor(column),
    progress_percent: column.progress_percent,
    counts_toward_progress: column.counts_toward_progress,
    marks_complete: column.marks_complete,
    visible_to_client: column.visible_to_client,
  }));
}

export function TaskStageEditor({ projectId = null, columns, onSaved, saveUrl, scope = 'project' }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftFromColumns(columns));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(draftFromColumns(columns));
  }, [columns]);

  const updateRow = (index, patch) => {
    setDraft((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const markDone = (index) => {
    setDraft((rows) =>
      rows.map((row, rowIndex) => ({
        ...row,
        marks_complete: rowIndex === index,
        counts_toward_progress: rowIndex === index ? true : row.counts_toward_progress,
        progress_percent: rowIndex === index ? 100 : row.progress_percent,
      })),
    );
  };

  const move = (index, direction) => {
    setDraft((rows) => {
      const next = [...rows];
      const target = index + direction;
      if (target < 0 || target >= next.length) return rows;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const remove = (index) => {
    setDraft((rows) => {
      if (rows.length <= 1) return rows;
      const next = rows.filter((_, rowIndex) => rowIndex !== index);
      if (!next.some((row) => row.marks_complete)) {
        const last = next.length - 1;
        next[last] = {
          ...next[last],
          marks_complete: true,
          counts_toward_progress: true,
          progress_percent: 100,
        };
      }
      return next;
    });
  };

  const add = () => {
    setDraft((rows) => {
      if (rows.length >= 12) return rows;
      return [
        ...rows,
        {
          id: null,
          name: 'New stage',
          color: nextStageColor(rows.map((row) => row.color)),
          progress_percent: 0,
          counts_toward_progress: true,
          marks_complete: false,
          visible_to_client: true,
        },
      ];
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const saved = await apiJson(saveUrl || `/api/admin/projects/${projectId}/task-columns`, {
        method: 'PUT',
        body: {
          columns: draft.map((row) => ({
            id: row.id || undefined,
            name: row.name.trim(),
            color: stageColor(row),
            progress_percent: row.marks_complete ? 100 : Number(row.progress_percent) || 0,
            counts_toward_progress: row.marks_complete ? true : Boolean(row.counts_toward_progress),
            marks_complete: Boolean(row.marks_complete),
            visible_to_client: Boolean(row.visible_to_client),
          })),
        },
      });
      onSaved?.(saved.columns || []);
      setOpen(false);
    } catch (err) {
      setError(err.message || 'Could not save stages');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <div className="task-stage-toolbar">
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          Edit stages
        </button>
        <p className="muted small">
          {scope === 'agency'
            ? 'These stages are shared by the agency task board and every project.'
            : 'Progress follows these stages and each task’s weight.'}
        </p>
      </div>
    );
  }

  return (
    <form className="task-stage-editor" onSubmit={save}>
      <div className="task-stage-editor-head">
        <div>
          <h3>Project stages</h3>
          <p className="muted small">
            {scope === 'agency'
              ? 'Rename, add, or remove stages for the whole agency. Saving updates every project. The done stage is always 100%.'
              : 'Rename stages, add or remove them, and set how complete a task is in each one. The done stage is always 100%. Cancelled-style stages can be left out of the percentage.'}
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => { setOpen(false); setDraft(draftFromColumns(columns)); setError(''); }}>
          Close
        </button>
      </div>
      {error ? <p className="err">{error}</p> : null}
      <div className="task-stage-list">
        {draft.map((row, index) => (
          <div className="task-stage-row" key={row.id || `new-${index}`}>
            <label className="field">
              <span>Name</span>
              <input value={row.name} maxLength={80} onChange={(event) => updateRow(index, { name: event.target.value })} required />
            </label>
            <StageColorField value={row.color} onChange={(color) => updateRow(index, { color })} />
            <label className="field">
              <span>Progress %</span>
              <input
                type="number"
                min="0"
                max="100"
                value={row.marks_complete ? 100 : row.counts_toward_progress ? row.progress_percent : 0}
                disabled={row.marks_complete || !row.counts_toward_progress}
                onChange={(event) => updateRow(index, { progress_percent: Number(event.target.value) })}
              />
            </label>
            <label className="task-stage-check">
              <input
                type="checkbox"
                checked={row.counts_toward_progress || row.marks_complete}
                disabled={row.marks_complete}
                onChange={(event) => updateRow(index, {
                  counts_toward_progress: event.target.checked,
                  progress_percent: event.target.checked ? row.progress_percent : 0,
                })}
              />
              Counts
            </label>
            <label className="task-stage-check">
              <input type="radio" name={`done-stage-${scope}-${projectId || 'agency'}`} checked={row.marks_complete} onChange={() => markDone(index)} />
              Done
            </label>
            <label className="task-stage-check">
              <input
                type="checkbox"
                checked={row.visible_to_client}
                onChange={(event) => updateRow(index, { visible_to_client: event.target.checked })}
              />
              Client
            </label>
            <div className="task-stage-row-actions">
              <button type="button" className="btn-secondary" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move stage up">
                ↑
              </button>
              <button type="button" className="btn-secondary" onClick={() => move(index, 1)} disabled={index === draft.length - 1} aria-label="Move stage down">
                ↓
              </button>
              <button type="button" className="btn-link" onClick={() => remove(index)} disabled={draft.length <= 1}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="task-stage-editor-actions">
        <button type="button" className="btn-secondary" onClick={add} disabled={draft.length >= 12}>
          Add stage
        </button>
        <button type="submit" className="btn-primary" disabled={saving || draft.some((row) => !row.name.trim())}>
          {saving ? 'Saving…' : 'Save stages'}
        </button>
      </div>
    </form>
  );
}
